import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import PolyAnimal from "@/components/PolyAnimal";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";

const PILLAR_NAMES = {
  S: "STRENGTH",
  F: "SPEED",
  E: "EXPLOSIVE",
  P: "STAMINA",
  C: "CONTROL",
};

export default function ArchetypeDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [arc, setArc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/archetypes/${code}`)
      .then((r) => setArc(r.data.archetype))
      .catch(() => setError("Archetype not found."))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-white gps-mono text-sm tracking-[0.3em]">SCANNING ARCHETYPE...</div>;
  }
  if (error || !arc) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="gps-mono text-mute">{error || "Archetype not found"}</div>
        <Link to="/archetypes" className="gps-btn mt-6 inline-flex">BACK</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid={`archetype-detail-${arc.code}`}>
      <button
        onClick={() => navigate("/archetypes")}
        className="gps-btn ghost mb-8"
        data-testid="archetype-back"
      >
        <ArrowLeft size={14} /> ALL ARCHETYPES
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: hero image */}
        <GlassPanel className="p-6 lg:p-10 grid place-items-center relative overflow-hidden min-h-[480px]">
          {arc.image_url ? (
            <img
              src={arc.image_url}
              alt={arc.name}
              className="w-full max-w-[520px] object-contain"
              style={{
                animation: "archetypeFloat 8s ease-in-out infinite",
                filter: "drop-shadow(0 0 30px rgba(255,255,255,0.18))",
              }}
              data-testid="archetype-hero-image"
            />
          ) : (
            <div className="flex flex-col items-center gap-6 text-center px-6">
              <PolyAnimal icon={arc.icon} size={300} spinDuration={45} />
              <div className="gps-mono text-mute text-[10px] tracking-[0.25em]">
                CUSTOM ARCHETYPE ART IN PRODUCTION
              </div>
            </div>
          )}
        </GlassPanel>

        {/* RIGHT: archetype details */}
        <div className="flex flex-col gap-4">
          <GlassPanel className="p-8">
            <div className="gps-tag mb-3">IDENTITY · {arc.code}</div>
            <h1 className="gps-heading text-5xl md:text-7xl leading-none">{arc.name.toUpperCase()}</h1>
            <div className="text-mute mt-3">{arc.tag}</div>
            <p className="text-white/85 leading-relaxed mt-6 text-base">{arc.description}</p>
          </GlassPanel>

          <GlassPanel className="p-6">
            <div className="gps-label mb-3">AXIS PROFILE</div>
            <div className="grid grid-cols-5 gap-2">
              {arc.axes.map((ax) => (
                <div
                  key={ax.pillar}
                  className={`text-center p-3 border rounded-lg ${
                    ax.dominant
                      ? "border-white/60 bg-white/5"
                      : "border-white/10 opacity-60"
                  }`}
                >
                  <div className="gps-mono text-xl text-white">{ax.letter}</div>
                  <div className="gps-mono text-[9px] text-mute mt-1 tracking-[0.18em]">
                    {PILLAR_NAMES[ax.letter.toUpperCase()]}
                  </div>
                  <div className="gps-mono text-[9px] mt-1 text-white">
                    {ax.dominant ? "DOMINANT" : "RECESSIVE"}
                  </div>
                </div>
              ))}
            </div>
          </GlassPanel>

          <GlassPanel className="p-6">
            <div className="gps-label mb-2">CHECK YOUR GPS</div>
            <p className="text-mute text-sm">
              Want to know if you're a {arc.name}? Submit your 10 test scores and the system will
              compute your archetype automatically.
            </p>
            <Link to="/my-gps" className="gps-btn mt-4 inline-flex" data-testid="archetype-cta-my-gps">
              CALCULATE
            </Link>
          </GlassPanel>
        </div>
      </div>
    </div>
  );
}
